/**
 * AccountMenu — the avatar menu in the header.
 * @file src/web/shared/components/AccountMenu.tsx
 *
 * @llm-rule WHEN: The signed-in person needs account actions or the Company Admin needs Authorities
 * @llm-rule NOTE: CLICK ONLY — no hover. Hover-to-open was measured closing
 *   before the pointer could reach the panel (4 items on hover, 0 by the time
 *   the cursor arrived), so nothing in it was ever clickable. Click is also
 *   the only thing that works on touch.
 */

import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  PasswordInput,
  toast,
} from '@bloomneo/uikit';
import { BarChart3, ChevronDown, Flag, KeyRound, LogOut, Receipt, UserCog } from 'lucide-react';
import { useAuth } from '../../features/auth';
import { useAppRequest } from '../hooks/useAppApi';
import { canBeFlagged, type OrgRole } from '../domain';
import { manageItemsFor } from './ManageMenu';
import { PersonAvatar } from './presence';

function ChangePasswordDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { request } = useAppRequest();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirmValue, setConfirmValue] = useState('');
  const [busy, setBusy] = useState(false);

  const mismatch = confirmValue.length > 0 && next !== confirmValue;
  const tooShort = next.length > 0 && next.length < 8;
  const ready = current && next && !mismatch && !tooShort && !busy;

  async function submit() {
    setBusy(true);
    try {
      const result = await request('/api/user/change-password', {
        method: 'POST',
        body: { currentPassword: current, newPassword: next },
      });

      if (!result.ok) {
        toast.error(result.error);
        return;
      }

      toast.success('Password changed');
      setCurrent('');
      setNext('');
      setConfirmValue('');
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Change your password</DialogTitle>
          <DialogDescription>
            You’ll stay signed in on this device. Anywhere else, you’ll need the new password.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <FormField label="Current password" required>
            <PasswordInput
              value={current}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setCurrent(e.target.value)}
              autoComplete="current-password"
            />
          </FormField>

          <FormField
            label="New password"
            required
            error={tooShort ? 'At least 8 characters' : undefined}
          >
            <PasswordInput
              value={next}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setNext(e.target.value)}
              autoComplete="new-password"
            />
          </FormField>

          <FormField
            label="Confirm new password"
            required
            error={mismatch ? 'These do not match' : undefined}
          >
            <PasswordInput
              value={confirmValue}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setConfirmValue(e.target.value)}
              autoComplete="new-password"
            />
          </FormField>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} disabled={!ready}>
            {busy ? 'Saving…' : 'Change password'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AccountMenu() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Dismiss on an outside click or Escape — the two things people already
  // expect from a menu.
  useEffect(() => {
    if (!open) return;
    function onDocClick(event: MouseEvent) {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false);
    }
    function onEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEscape);
    };
  }, [open]);

  if (!user) return null;

  const role = (user.orgRole ?? 'EMPLOYEE') as OrgRole;

  const manageItems = manageItemsFor(role);
  const manageHrefs = new Set(manageItems.map((i) => i.href));

  const groupLabelClass =
    'px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-[0.1em] text-ink-ghost';

  const itemClass =
    'flex w-full items-center gap-2.5 px-3 py-2 text-left text-[12.5px] text-ink-soft transition-colors hover:bg-paper-warm hover:text-ink';

  return (
    <>
      <div ref={wrapRef} className="relative">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label="Account menu"
          className="flex items-center gap-1 rounded-full border border-rule py-0.5 pl-0.5 pr-1.5 transition-colors hover:bg-paper-warm"
        >
          <PersonAvatar
            person={{ id: user.id, name: user.name ?? user.email, initials: user.initials }}
            size={26}
          />
          <ChevronDown
            className={`size-3.5 text-ink-faint transition-transform ${open ? 'rotate-180' : ''}`}
          />
        </button>

        {open && (
          // No gap between trigger and panel — a gap makes the menu close as
          // the pointer travels to it.
          <div role="menu" className="absolute right-0 top-full z-50 w-60 pt-2">
            <div className="overflow-hidden rounded-xl border border-rule bg-paper py-1 shadow-[0_12px_32px_rgba(20,24,31,0.14)]">
              <div className="border-b border-rule px-3 pb-2 pt-1.5">
                <div className="truncate text-[13px] font-medium text-ink">{user.name}</div>
                <div className="truncate text-[11.5px] text-ink-faint">{user.email}</div>
                <div className="mt-1 inline-block rounded-full bg-paper-sunk px-2 py-0.5 text-[10.5px] font-medium text-ink-faint">
                  {user.title || role}
                </div>
              </div>

              {/*
                The company tools, previously behind a second "Manage" dropdown
                next to this one. Two triggers side by side gave no clue which
                held what; one menu with headings does.
              */}
              {manageItems.length > 0 && (
                // DESKTOP ONLY. On a phone these same entries are the "More"
                // tab in the bottom bar, and listing them here as well gave an
                // admin two routes to one place with no way to tell which was
                // canonical. Above `md` there is no bottom bar, so this is the
                // only way in and it stays.
                <div className="hidden md:block">
                  <div className={groupLabelClass}>Manage</div>
                  {manageItems.map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      role="menuitem"
                      className={itemClass}
                      onClick={() => setOpen(false)}
                    >
                      <item.icon className="size-4" />
                      {item.label}
                    </Link>
                  ))}
                  <div className="my-1 border-t border-rule" />
                </div>
              )}

              <div className={groupLabelClass}>You</div>

              {/* Shown here only when it is NOT already in Manage above —
                  otherwise a Manager saw the same link twice in one menu. */}
              {!manageHrefs.has('/reports') && (
                <Link to="/reports" role="menuitem" className={itemClass} onClick={() => setOpen(false)}>
                  <BarChart3 className="size-4" />
                  Monthly report
                </Link>
              )}

              {/* Everyone can read their own payslips, but only Heads and the
                  admin get the management entry above. */}
              {!manageHrefs.has('/payslips') && (
                <Link to="/payslips" role="menuitem" className={itemClass} onClick={() => setOpen(false)}>
                  <Receipt className="size-4" />
                  My payslips
                </Link>
              )}

              {/* Heads and the Company Admin are never flagged by anyone — see
                  canBeFlagged — so the page would always be empty for them. */}
              {canBeFlagged(role) && (
                <Link to="/record" role="menuitem" className={itemClass} onClick={() => setOpen(false)}>
                  <Flag className="size-4" />
                  My flags
                  <span className="ml-auto text-[10.5px] text-ink-ghost">praise · off-site</span>
                </Link>
              )}

              <Link to="/profile" role="menuitem" className={itemClass} onClick={() => setOpen(false)}>
                <UserCog className="size-4" />
                My profile
              </Link>

              <button
                type="button"
                role="menuitem"
                className={itemClass}
                onClick={() => {
                  setOpen(false);
                  setPasswordOpen(true);
                }}
              >
                <KeyRound className="size-4" />
                Change password
              </button>

              <div className="my-1 h-px bg-rule" />

              <button
                type="button"
                role="menuitem"
                className={`${itemClass} font-medium text-flag hover:bg-[var(--state-bad-bg)] hover:text-flag`}
                onClick={() => logout()}
              >
                <LogOut className="size-4" />
                Sign out
              </button>
            </div>
          </div>
        )}
      </div>

      <ChangePasswordDialog open={passwordOpen} onOpenChange={setPasswordOpen} />
    </>
  );
}
